import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { format } from 'date-fns';
import { APIProvider, Map } from '@vis.gl/react-google-maps';
import { useHeaderContext } from "../../../context/headerContext";
import { useDrawerContext } from "../../../context/drawerContext";
import { useUserContext } from "../../../context/userContext";
import generateUUID from "../../../utils/generateUUID";
import { fetchUserProfile } from '../../../utils/FetchUserProfile';
import { fetchAdditionalICEContacts } from '../../../utils/FetchAdditionalICEContacts';
import { fetchMedicalHistory } from '../../../utils/FetchMedicalHistory';
import { fetchAllergies } from '../../../utils/FetchAllergies';
import { fetchMedications } from '../../../utils/FetchMedications';
import { fetchHistoricalIncidentsForParticipant } from '../../../utils/FetchHistoricalIncidentsForParticipant';
import { fetchBand } from '../../../utils/FetchBand';
import { fetchIncident } from '../../../utils/FetchIncident';
import { toast } from "react-toastify";
import { fetchIncidentNotes } from '../../../utils/FetchIncidentNotes';

import BandCard from '../../../components/BandCard';
import DropDown from '../../../components/Dropdown';
import Button from '../../../components/Button';
import TextArea from '../../../components/TextArea';
import VoiceNote from '../../../components/VoiceNote'

import CloseIcon from "/public/images/icons/close.svg?react";
import ChevronRight from "/public/images/icons/chevronRight.svg?react";
import IncidentIcon from "/public/images/icons/incidentHistoryIcon.svg?react";
import fetchWithRefresh from '../../../utils/fetchWithRefresh';

import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../utils/LogErrors";

  export default function Incident() {
    const { setTitle, setBackButton, setEmergency } = useHeaderContext();
    const { setOpen, setDrawerContent } = useDrawerContext();
    const { state } = useLocation();
    const { user, selectedVenue, selectedZone, logout, updateTokens } = useUserContext();
    const [participantBand, setParticipantBand] = useState(null);
    const [participantProfile, setParticipantProfile] = useState(null);
    const [incidentData, setIncidentData] = useState({});
    const [incidentNotes, setIncidentNotes] = useState([]);
    const [currentNote, setCurrentNote] = useState('');
    const [medicalHistory, setMedicalHistory] = useState([]);
    const [allergies, setAllergies] = useState([]);
    const [medications, setMedications] = useState([]);
    const [otherEmergencyContacts, setOtherEmergencyContacts] = useState([]);
    const [incidentHistory, setIncidentHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawerKey, setDrawerKey] = useState(0);

    const { incidentId } = useParams();
    const incident = state?.user;
    const navigate = useNavigate();
    const timeoutRef = useRef(null);

    const fetchCurrentIncident = async () => {
      const data = await fetchIncident(incidentId, user?.token);
      setIncidentData(data);
    }; 

    useEffect(() => {
      setTitle('LIVE INCIDENT');
      setBackButton(true);
      setEmergency(true);
      fetchCurrentIncident();
    }, [incidentId]);

    const fetchProfile = async () => {
      const data = await fetchUserProfile(participantBand?.ParticipantId, user?.token);
      setParticipantProfile(data);
    }; 

    const fetchUserAdditionalICEContacts = async () => {
      const data = await fetchAdditionalICEContacts(participantBand?.ParticipantId, user?.token);
      setOtherEmergencyContacts(data);
    }

    const fetchUserMedicalHistory = async () => {
      const data = await fetchMedicalHistory(participantBand?.ParticipantId, user?.token);
      setMedicalHistory(data);
    }

    const fetchUserAllergies = async () => {
      const data = await fetchAllergies(participantBand?.ParticipantId, user?.token);
      setAllergies(data);
    }

    const fetchUserMedications = async () => {
      const data = await fetchMedications(participantBand?.ParticipantId, user?.token);
      setMedications(data);
    }

    const fetchUserIncidents = async () => {
      const data = await fetchHistoricalIncidentsForParticipant(participantBand?.ParticipantId, user?.token);
      setIncidentHistory(data);
    }

    const fetchUserBand = async () => {
      const data = await fetchBand(incidentData?.BandID, user?.token);
      setParticipantBand(data);
    }

    const fetchCurrentIncidentNotes = async () => {
      const data = await fetchIncidentNotes(incidentId, user?.token);
      setIncidentNotes(data);
    }

    useEffect(() => {
      if(incidentData?.BandID){
        fetchUserBand();
        fetchCurrentIncidentNotes();
      }
    }, [incidentData]);

    useEffect(() => {
      if(participantBand){
        fetchProfile(),
        fetchUserAdditionalICEContacts(),
        fetchUserMedicalHistory(),
        fetchUserAllergies(),
        fetchUserMedications(),
        fetchUserIncidents()
        setLoading(false);
      }
    }, [participantBand]);

    useEffect(() => { 
      if(currentNote) {
        liveIncidentNotes();
      }
    }, [currentNote]);
  
    const handleAddNote = async () => {
      const noteId = generateUUID();
    
      await Sentry.startSpan({ name: "Add Incident Note", op: "http.request" }, async (span) => {
        try {
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}Incident/AddIncidentNote`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user?.token}`,
              },
              body: JSON.stringify({
                emergencyId: incidentData?.IncidentId,
                noteId: noteId,
                note: currentNote,
                noteCreated: new Date().toISOString(),
                userLoginId: user?.id,
              }),
            },
            logout,
            updateTokens,
            (message) => {
              toast.info(message);
            }
          );
    
          span.setAttribute("http.status_code", response.status);
    
          if (response.ok) {
            const newNote = {
              NoteId: noteId,
              Note: currentNote,
              NoteCreated: new Date().toISOString(),
              UserLoginId: user?.id,
            };
    
            setIncidentNotes((prevNotes) => [newNote, ...prevNotes]);
            setCurrentNote("");
            setOpen(false);
            setDrawerKey((prevKey) => prevKey + 1); // triggers a re-render of the drawer
          } else {
            throw new Error("Failed to add note");
          }
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error adding incident note: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };    
  
    const liveIncidentNotes = () => {
      setCurrentNote(''); // Reset the text area
      setOpen(true); // Open the drawer to add a note
      setDrawerContent(
        <div key={drawerKey} className="w-full">
          <div className="flex items-center justify-between w-full">
            <br />
            <h2 className="leading-5 tracking-widest uppercase text-[12px]">
              + ADD NOTE
            </h2>
            <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
          </div>
          <div className="mt-6 flex flex-col gap-4 justify-stretch">
            <h3 className="text-[24px]">Add a note to this live incident</h3>
            <p className="text-sportsDarkGrey">
              This note will appear under Additional Notes on the Live Incident.
            </p>
            <TextArea
              label="Live Incident Notes"
              name="note"
              onChange={(e) => setCurrentNote(e.target.value)}
            />
            <Button variant="primary" onClick={() => handleAddNote()}>
              + Add Note
            </Button>
          </div>
        </div>
      );
    };

    const closeIncident = () => {
      setOpen(true);
      setDrawerContent(
        <div className="w-full">
          <div className="flex items-center justify-between w-full">
            <br />
            <h2 className="leading-5 tracking-widest uppercase text-[12px]">
              CLOSE INCIDENT
            </h2>
            <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
          </div>
          <form onSubmit={onCloseSubmit} className="mt-6 flex flex-col gap-4 justify-stretch">
            <h3 className="text-[24px]">Close this live incident, are you sure?</h3>
            <p className="text-sportsDarkGrey">
              Please provide details on how the incident was closed. It will become archived.
            </p>

            <TextArea
              name="closureNotes"
              label="Closure Notes"
              required
            />

            <Button variant="primary" submit>Close Incident</Button>
          </form>
        </div>
      );
    }

    const onCloseSubmit = async (event) => {
      event.preventDefault();
      const newDetails = new FormData(event.currentTarget);
      const closingNote = newDetails.get("closureNotes");
    
      await Sentry.startSpan({ name: "Close Incident", op: "http.request" }, async (span) => {
        try {
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}Incident/CloseIncident`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user?.token}`,
              },
              body: JSON.stringify({
                emergencyId: `${incidentData?.IncidentId}`,
                closedById: user?.id,
                closingNote: closingNote,
                venueId: `${selectedVenue?.OrgID}`,
                zoneId: `${selectedZone.ZoneID}`,
              }),
            },
            logout,
            updateTokens,
            (message) => {
              toast.info(message);
            }
          );
    
          span.setAttribute("http.status_code", response.status);
    
          if (response.ok) {
            setOpen(false);
            setDrawerContent(<div></div>);
            timeoutRef.current = setTimeout(() => {
              navigate("/");
            }, 1000);
          } else {
            setDrawerContent(
              <div className="w-full">
                <div className="flex items-center justify-between w-full">
                  <br />
                  <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                    ERROR CLOSING INCIDENT
                  </h2>
                  <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false); navigate("/")}} />
                </div>
                <div className="mt-6 flex flex-col gap-4 justify-stretch">
                  <h3 className="text-[24px]">This incident is already closed.</h3>
                  <Button variant="primary" className="w-full" href="/" onClick={() => {setOpen(false)}}>Go Home</Button>
                </div>
              </div>
            );
          }
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error closing incident: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };    

    // Clear the timeout if the component unmounts
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);
  
    return (
      <div className='mt-[20px]'>
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <div className="loading loading-spinner loading-lg">Loading...</div>
        </div>
      ) : (
        <>
          <div className='p-[24px] mb-[15px]'>
            <BandCard size="sm" band={participantBand} participant={incidentData} type='participant' />
            <div className='mt-[60px] mb-[30px] pb-[30px] border-b border-dashed border-[#E3E3E3]'>
              <div>
                <p className='font-poppins font-semibold text-[24px]'>{incidentData?.ParticipantName}</p>
                <p className='font-poppins text-[#727272]'>Emergency Scan • Last Updated • {incidentData?.LastUpdated ? format(new Date(incidentData?.LastUpdated), 'dd/MM/yyyy') : ''}</p>
              </div>
              <div className='flex flex-row gap-x-4 justify-between my-[30px]'>
                <div className='flex flex-col gap-y-1'>
                  <p className='font-poppins font-semibold text-[16px]'>Venue</p>
                  <p className='font-poppins text-[#727272] capitalize'>{selectedVenue?.OrgPartnerFriendlyName}</p>
                </div>
                <div className='flex flex-col gap-y-1'>
                  <p className='font-poppins font-semibold text-[16px]'>Zone</p>
                  <p className='font-poppins text-[#727272] capitalize'>{incidentData?.ZoneName}</p>
                </div>
              </div>
              {((incident?.Lat != "null" || incidentData?.Lat != "null") && (incident?.Lng != "null" || incidentData?.Lng != "null")) && (
                <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
                  <Map
                    style={{width: '100%', height: '174px', borderRadius: '4px', border: '1px solid #E3E3E3'}}
                    defaultCenter={{
                      lat: parseFloat(incident?.Lat || incidentData?.Lat), 
                      lng: parseFloat(incident?.Lng || incidentData?.Lng)
                    }}
                    defaultZoom={16}
                    gestureHandling={'greedy'}
                    disableDefaultUI={true}
                  />
                </APIProvider>
              )}
            </div>
            <div className='flex flex-col gap-4'>
              <div className='flex flex-col gap-y-1'>
                <p className='font-poppins font-semibold text-[16px]'>Date of Birth</p>
                <p className='font-poppins text-[#727272]'>{participantProfile?.DateOfBirth ? format(participantProfile?.DateOfBirth, 'dd/MM/yyyy') : ''}</p>
              </div>
              <div className='flex flex-col gap-y-1'>
                <p className='font-poppins font-semibold text-[16px]'>Primary Emergency Contact Name (ICE)</p>
                <p className='font-poppins text-[#727272]'>{participantProfile?.PrimaryIceName}</p>
              </div>
              <div className='flex flex-row gap-y-1 justify-between'>
                <div>
                  <p className='font-poppins font-semibold text-[16px]'>Primary Emergency Contact Number</p>
                  <Link to={`tel:${participantProfile?.PrimaryIceNumber}`} className='font-poppins text-[#727272] underline'>{participantProfile?.PrimaryIceNumber}</Link>
                </div>
                <div className='flex flex-col justify-center items-center'>
                  <Link to={`tel:${participantProfile?.PrimaryIceNumber}`} className='flex items-center gap-x-2 text-sportsBlue'>
                    <img src='/images/icons/phone.svg' alt='phone' className='w-[24px] h-[24px]' />
                  </Link>
                </div>
              </div>
              <div className='flex flex-col gap-y-1'>
                <p className='font-poppins font-semibold text-[16px]'>Primary Emergency Contact Relationship</p>
                <p className='font-poppins text-[#727272]'>{participantProfile?.PrimaryIceRelationship}</p>
              </div>
              <div className='flex flex-col gap-y-1'>
                <p className='font-poppins font-semibold text-[16px]'>Primary Medical Condition</p>
                <p className='font-poppins text-[#727272]'>{participantProfile?.PrimaryMedicalCondition}</p>
              </div>

              {participantProfile?.AudioUrl && (
                <div className='flex flex-col gap-y-1'>
                  <p className='font-poppins font-semibold text-[16px]'>Voice Note</p>
                  <VoiceNote existingAudio={participantProfile?.AudioUrl} label={false} />
                </div>
              )}

              <div className='flex flex-col gap-y-1'>
                <p className='font-poppins font-semibold text-[16px]'>Notes</p>
                <p className='font-poppins text-[#727272]'>{participantProfile?.Notes}</p>
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-y-[10px] bg-sportsLightGrey p-[24px]'>
            <div className='flex flex-col gap-y-[20px] border-b border-dashed mb-[10px] pb-[30px]'>
              <div>
                <p className="font-poppins font-semibold text-[16px] mb-[10px]">Live Incident Notes</p>
                {Array.isArray(incidentNotes) && incidentNotes.length > 0 ? (
                  <ul className='flex flex-col gap-y-[10px]'>
                    
                    {incidentNotes.map((note, index) => {
                      return (
                        <li className='font-poppins text-[#727272]' key={index}>{note.Note}</li>
                      )
                    })}
                  </ul>
                ) : incidentNotes && !Array.isArray(incidentNotes) ? (
                  <ul className='flex flex-col gap-y-[10px]'>
                    <li className='font-poppins text-[#727272]' key={incidentNotes.NoteId}>{incidentNotes.Note}</li>
                  </ul>
                ) : (
                  <p className="font-poppins text-[#727272]">
                    Currently there are no Live Incident Notes, tap the button below to add
                  </p>
                )}
              </div>
              <Button variant='primary' onClick={liveIncidentNotes}>+ Add Note</Button>
            </div>
            {medicalHistory.length > 0 && (
              <DropDown title='Medical History' icon="medical">
                <div className='flex flex-col gap-y-3'>
                  {medicalHistory?.map((medicalHistory, index) => (
                    <div key={index} className='flex flex-col'>
                      <p className='font-poppins text-[16px] font-semibold'>{medicalHistory.ConditionName}</p>
                      <p className='font-poppins text-[#727272]'>
                        {format(new Date(medicalHistory?.DateAdded), 'dd/MM/yyyy')}
                      </p>
                      <p className='font-poppins text-[#727272]'>Notes: {medicalHistory.Notes}</p>
                      <p className='font-poppins text-[#727272]'>Ongoing: {medicalHistory.OnGoing ? "Yes" : "No"}</p>
                    </div>
                  ))}
                </div>
              </DropDown>
            )}
            {allergies.length > 0 && (
              <DropDown title="Allergies" icon="allergy">
                <div className='flex flex-col gap-y-3'>
                  {allergies?.map((allergy, index) => (
                    <div key={index} className='flex flex-col'>
                      <p className='font-poppins font-semibold text-[16px]'>{allergy?.ConditionName}</p>
                      <p className='font-poppins text-[#727272]'>Added: {format(new Date(allergy.DateAdded), 'dd/MM/yyyy HH:mm:ss')}</p>
                      <p className='font-poppins text-[#727272]'>Notes: {allergy.Notes}</p>
                      <p className='font-poppins text-[#727272]'>Ongoing: {allergy.OnGoing ? "Yes" : "No"}</p>
                    </div>
                  ))}
                </div>
              </DropDown>
            )}
            {medications.length > 0 && (
              <DropDown title="Medications" icon="medication">
                <div className='flex flex-col gap-y-3'>
                  {medications?.map((medication, index) => (
                    <div key={index} className='flex flex-col'>
                      <p className='font-poppins text-[16px] font-semibold'>{medication.MedicationName}</p>
                      {medication.Dosage && <p className='font-poppins text-[#727272]'>Dosage: {medication.Dosage}</p>}
                      {medication.Frequency && <p className='font-poppins text-[#727272]'>Frequency: {medication.Frequency}</p>}
                      {medication.Notes && <p className='font-poppins text-[#727272]'>Notes: {medication.Notes}</p>}
                      <p className='font-poppins text-[#727272]'>Ongoing: {medication.OnGoing ? "Yes" : "No"}</p>
                    </div>
                  ))}
                </div>
              </DropDown>
            )}
            {otherEmergencyContacts.length > 0 && (
              <DropDown title="Other Emergency Contacts" icon="contact">
                <div className='flex flex-col gap-y-3'>
                  {otherEmergencyContacts?.map((contact, index) => (
                    <div key={index} className='flex flex-col'>
                      <p className='font-poppins font-normal text-[16px]'>{contact.AdditionalICEName}</p>
                      <p className='font-poppins text-[#727272]'>{contact.AdditionalICERelationship}</p>
                      <Link to={`tel:${contact.AdditionalICENumber}`} className='font-poppins text-[#727272] underline'>{contact.AdditionalICENumber}</Link>
                    </div>
                  ))}
                </div>
              </DropDown>
            )}
            {incidentHistory.length > 0 && (
              <Link to={`/participant/${participantProfile?.ParticipantId}/incident-history`}>
                <div
                  className={`flex flex-col justify-between bg-white border rounded-[8px] p-6`}
                  data-testid="dropdown-container"
                >
                  <div className={`flex justify-between min-w-full`}>
                    <div className="flex flex-row items-center gap-x-[20px]">
                      <IncidentIcon className="w-[24px] h-[24px]" data-testid="icon" />
                      <div className="flex cursor-pointer flex-row w-full justify-between items-center font-semibold antialiased">
                        Incident History
                      </div>
                    </div>
                    <div className="">
                      <ChevronRight
                        data-testid="dropdown-icon"
                        className={`fill-[#000000] transition-all cursor-pointer`}
                      />
                    </div>
                  </div>
                </div>
              </Link>
            )}
          </div>
          <div className='flex flex-col pt-[24px] pb-[75px] gap-y-[10px] p-y-[31px] px-[24px] bg-sportsLightGrey'>
            <Button variant='primary' className='' onClick={closeIncident}>Close Incident</Button>
          </div>
        </>
      )}
    </div>
    );
  }